/**
 * @fileoverview Picking pictures out of Google Photos from a dialog: where the pick has
 * got to, in words, and its failures in words.
 *
 * One tap opens Google Photos (GooglePhotosService opens the window inside
 * the tap). While the gardener chooses, the row says so and offers Cancel;
 * while the photos come over, it counts them. A failure is said in a plain
 * sentence under the row; a cancel is not a failure and says nothing.
 *
 * Closing the dialog, or leaving its page, lets the pick go without stopping
 * it (2026-10-11): on a phone the dialog may be gone, or the whole tab
 * reloaded, by the time the gardener taps Done in Google Photos, and that
 * used to throw the photos away. They go to the Photos tray instead
 * (GooglePhotosService's fallback, GooglePhotosResume), on the target's bed
 * when it has one. Only Cancel, or a new tap, stops a pick.
 *
 * Where: EditPhotoDialog, UploadPhotoDialog, TrayUploader,
 * EditLocationDialog, TodayPhotoDialog and ProfilePhotoCard, through
 * PhotoSourcePicker.
 * API: Google's own picker, through GooglePhotosService; nothing of ours.
 * Settings: VITE_GOOGLE_CLIENT_ID; unset, the row says Google Photos is
 * unavailable.
 * Words (en/es): plants.photo.source.* (googleUnavailable, googleFailed,
 * googleNoAnswer).
 *
 * @module useGooglePhotos
 */

import { t } from '@/shared/i18n/i18nSetup';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GooglePhotosService, GooglePhotosError,
  type GooglePhotoPick, type GooglePhotosFailure, type GooglePhotosStage, type PickTarget,
} from '@/shared/services/GooglePhotosService';
import { debugLog } from '@/config/config';

/**
 * What the gardener is told for each way Google Photos can fail.
 *
 * The service's own messages are for the logs: they quote Google's status
 * codes and point at the Cloud console. 'cancelled' never reaches here.
 */
const FAILURE_KEYS: Record<GooglePhotosFailure, string> = {
  'not-configured': 'plants.photo.source.googleUnavailable',
  'popup-blocked': 'plants.photo.source.googleBlocked',
  'consent-denied': 'plants.photo.source.googleDeclined',
  'scope-denied': 'plants.photo.source.googleDeclined',
  'session-failed': 'plants.photo.source.googleNoAnswer',
  'signed-out': 'plants.photo.source.googleSignIn',
  'nothing-sent': 'plants.photo.source.googleNothingSent',
  'download-failed': 'plants.photo.source.googleFetchFailed',
  'timed-out': 'plants.photo.source.googleTimedOut',
  'cancelled': 'plants.photo.source.googleFailed',
  'unknown': 'plants.photo.source.googleFailed',
};

/**
 * A plain sentence for a failed pick.
 *
 * A fetch that never reached Google throws a TypeError rather than one of
 * the service's errors; from the gardener's chair that is Google not
 * answering, and is said so.
 *
 * @param err - Whatever the pick threw.
 * @returns The sentence, translated.
 */
export function failureMessage(err: unknown): string {
  if (err instanceof GooglePhotosError) return t(FAILURE_KEYS[err.reason]);
  if (err instanceof TypeError) return t('plants.photo.source.googleNoAnswer');
  return t('plants.photo.source.googleFailed');
}

/** Where a pick has got to: nothing under way, or one of the service's stages. */
export type GooglePhotosStatus = 'idle' | GooglePhotosStage;

/** What a dialog gets back: where the pick has got to, and its verbs. */
export interface GooglePhotosPicker {
  /** True while a pick is under way, from the tap until the photos are in. */
  isPicking: boolean;
  /** Where the pick has got to. */
  status: GooglePhotosStatus;
  /** How many photos are coming over, while fetching. */
  count: number;
  /** What went wrong last time, in words, or null. */
  error: string | null;
  /**
   * Opens Google Photos. Call it straight from the tap: the window opens
   * before anything is awaited. Resolves with the chosen pictures and the
   * day Google says each was taken; empty when none were chosen or the pick
   * was cancelled.
   *
   * @param many - Whether to bring back every picture picked, not just the first.
   */
  pick: (many?: boolean) => Promise<GooglePhotoPick[]>;
  /** Stops the pick under way; it ends quietly with nothing. */
  cancel: () => void;
  /** Clears the error without opening anything. */
  clearError: () => void;
}

/** A pick with nowhere particular to go: its photos go to the tray if the dialog is gone. */
const NO_TARGET: PickTarget = { kind: 'other' };

/**
 * Google Photos for one dialog.
 *
 * @param isOpen - Whether the dialog is open; closing it lets a pick go to
 *   the tray rather than stopping it.
 * @param target - Where the photos are going (a plant, a bed, the tray, a
 *   packet scan), kept with the pick so they still arrive somewhere sensible
 *   when the dialog is gone.
 * @returns The pick's state and verbs.
 */
export function useGooglePhotos(isOpen: boolean, target: PickTarget = NO_TARGET): GooglePhotosPicker {
  /** The one Google Photos service the app shares. */
  const service = useMemo(() => GooglePhotosService.getInstance(), []);
  const [status, setStatus] = useState<GooglePhotosStatus>('idle');
  const [count, setCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  /**
   * The pick this dialog started, so it cancels its own and not another
   * dialog's. A plain holder rather than a ref: it is read in a cleanup.
   */
  const mine = useMemo<{ current: Promise<GooglePhotoPick[]> | null }>(() => ({ current: null }), []);
  /** The target as last rendered, read at the tap without making pick change with it. */
  const where = useRef<PickTarget>(target);
  where.current = target;

  /**
   * Opens Google Photos, or says why it cannot.
   *
   * @param many - Bring back every picture picked, not only the first.
   */
  const pick = useCallback((many = false): Promise<GooglePhotoPick[]> => {
    if (!service.isConfigured()) {
      // Said to the gardener in their words; the missing setting is a
      // deployment's business, and is logged where a developer will see it.
      debugLog('useGooglePhotos', 'Google Photos is not configured: VITE_GOOGLE_CLIENT_ID is unset.');
      setError(t('plants.photo.source.googleUnavailable'));
      return Promise.resolve([]);
    }

    // Straight to the service, nothing awaited first, so the window opens
    // inside this tap.
    const attempt = service.showPicker(many, (progress) => {
      setStatus(progress.stage);
      setCount(progress.count);
    }, where.current);
    setError(null);
    mine.current = attempt;

    return attempt
      .catch((err: unknown): GooglePhotoPick[] => {
        debugLog('useGooglePhotos', 'Google Photos selection error:', err);
        const reason = err instanceof GooglePhotosError ? err.reason : 'unknown';
        if (reason !== 'cancelled') setError(failureMessage(err));
        return [];
      })
      .finally(() => {
        // A newer tap's pick is left to say its own state.
        if (mine.current !== attempt) return;
        mine.current = null;
        setStatus('idle');
        setCount(0);
      });
  }, [service, mine]);

  /** Stops this dialog's pick: the gardener's Cancel. */
  const cancel = useCallback(() => {
    if (mine.current) service.cancel(mine.current);
  }, [service, mine]);

  // Closing the dialog lets its pick go on without it: the photos go to the tray.
  useEffect(() => {
    if (!isOpen && mine.current) service.detach(mine.current);
  }, [isOpen, service, mine]);

  // So does leaving the page it is on.
  useEffect(() => () => {
    if (mine.current) service.detach(mine.current);
  }, [service, mine]);

  /** Forgets the last failure. */
  const clearError = useCallback(() => setError(null), []);

  return { isPicking: status !== 'idle', status, count, error, pick, cancel, clearError };
}
